import { stockAdjustmentReasonSchema, stockReasonSchema } from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

export const STOCK_PAGE_SIZES = [25, 50, 100] as const
/** Movements are read in longer runs: 50 by default, like the API. */
export const MOVEMENT_PAGE_SIZES = [50, 100, 200] as const

const pageSizeOf = <T extends readonly number[]>(sizes: T) =>
  z
    .number()
    .refine((n): n is T[number] => (sizes as readonly number[]).includes(n))
    .optional()
    .catch(undefined)

const page = z.number().int().min(1).optional().catch(undefined)

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

// ── stock levels ─────────────────────────────────────────────────────────────

/** The stock levels list lives in the URL; invalid values fall back to the default. */
export const stockSearchSchema = z.object({
  page,
  pageSize: pageSizeOf(STOCK_PAGE_SIZES),
  q: z.string().trim().max(200).optional().catch(undefined),
  warehouseId: z.uuid().optional().catch(undefined),
  belowReorder: z.boolean().optional().catch(undefined),
})
export type StockSearch = z.infer<typeof stockSearchSchema>

export interface StockListParams {
  page: number
  pageSize: number
  q: string | undefined
  warehouseId: string | undefined
  belowReorder: boolean | undefined
}

export function toStockListQuery(search: StockSearch): StockListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? STOCK_PAGE_SIZES[0],
    q: search.q || undefined,
    warehouseId: search.warehouseId,
    belowReorder: search.belowReorder || undefined,
  }
}

// ── stock movements ──────────────────────────────────────────────────────────

export const movementSearchSchema = z.object({
  page,
  pageSize: pageSizeOf(MOVEMENT_PAGE_SIZES),
  variantId: z.uuid().optional().catch(undefined),
  /** Every variant of one product (the product 360's Stock tab links here). */
  productId: z.uuid().optional().catch(undefined),
  reason: stockReasonSchema.optional().catch(undefined),
  warehouseId: z.uuid().optional().catch(undefined),
  from: isoDate,
  to: isoDate,
})
export type MovementSearch = z.infer<typeof movementSearchSchema>

export interface MovementListParams {
  page: number
  pageSize: number
  variantId: string | undefined
  productId?: string | undefined
  reason?: z.infer<typeof stockReasonSchema> | undefined
  warehouseId: string | undefined
  from: string | undefined
  to: string | undefined
}

export function toMovementListQuery(search: MovementSearch): MovementListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? MOVEMENT_PAGE_SIZES[0],
    variantId: search.variantId,
    productId: search.productId,
    reason: search.reason,
    warehouseId: search.warehouseId,
    from: search.from,
    to: search.to,
  }
}

// ── stock adjustments ────────────────────────────────────────────────────────

export const ADJUSTMENT_PAGE_SIZES = [25, 50, 100] as const
const ADJUSTMENT_SORT_FIELDS = ['adjustmentDate', 'number'] as const
export type AdjustmentSort =
  | (typeof ADJUSTMENT_SORT_FIELDS)[number]
  | `-${(typeof ADJUSTMENT_SORT_FIELDS)[number]}`
export const ADJUSTMENT_DEFAULT_SORT = '-adjustmentDate'

export function isAdjustmentSort(value: string): value is AdjustmentSort {
  return (ADJUSTMENT_SORT_FIELDS as readonly string[]).includes(
    value.startsWith('-') ? value.slice(1) : value,
  )
}

/** The list's views: every document, one per kind (posted), and the void ones (R-2). */
export const ADJUSTMENT_VIEWS = ['all', 'opening', 'adjustment', 'count', 'void'] as const
export type AdjustmentView = (typeof ADJUSTMENT_VIEWS)[number]

/** The adjustments list lives in the URL; invalid values fall back to the default. */
export const adjustmentSearchSchema = z.object({
  page,
  pageSize: pageSizeOf(ADJUSTMENT_PAGE_SIZES),
  sort: z.string().refine(isAdjustmentSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  view: z.enum(ADJUSTMENT_VIEWS).optional().catch(undefined),
  reason: stockAdjustmentReasonSchema.optional().catch(undefined),
  warehouseId: z.uuid().optional().catch(undefined),
  from: isoDate,
  to: isoDate,
})
export type AdjustmentSearch = z.infer<typeof adjustmentSearchSchema>

export interface AdjustmentListParams {
  page: number
  pageSize: number
  sort: string
  q: string | undefined
  kind: 'OPENING' | 'ADJUSTMENT' | 'COUNT' | undefined
  reason: z.infer<typeof stockAdjustmentReasonSchema> | undefined
  status: 'POSTED' | 'VOID' | undefined
  warehouseId: string | undefined
  from: string | undefined
  to: string | undefined
}

const VIEW_FILTER: Record<AdjustmentView, Pick<AdjustmentListParams, 'kind' | 'status'>> = {
  all: { kind: undefined, status: undefined },
  opening: { kind: 'OPENING', status: 'POSTED' },
  adjustment: { kind: 'ADJUSTMENT', status: 'POSTED' },
  count: { kind: 'COUNT', status: 'POSTED' },
  void: { kind: undefined, status: 'VOID' },
}

export function toAdjustmentListQuery(search: AdjustmentSearch): AdjustmentListParams {
  const view = search.view ?? 'all'
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? ADJUSTMENT_PAGE_SIZES[0],
    sort: search.sort ?? ADJUSTMENT_DEFAULT_SORT,
    q: search.q || undefined,
    ...VIEW_FILTER[view],
    // Only adjustments carry a reason: the filter narrows the All and Adjustments views.
    reason: view === 'all' || view === 'adjustment' ? search.reason : undefined,
    warehouseId: search.warehouseId,
    from: search.from,
    to: search.to,
  }
}

/**
 * New stock adjustment: `?warehouseId=` preselects the warehouse, `?productId=` fills the lines with
 * the product's active variants and `?kind=` picks the kind (Adjust stock and Count from a product).
 */
export const newAdjustmentSearchSchema = z.object({
  warehouseId: z.uuid().optional().catch(undefined),
  productId: z.uuid().optional().catch(undefined),
  /** With `productId`: only this variant (a stock level row). */
  variantId: z.uuid().optional().catch(undefined),
  kind: z.enum(['OPENING', 'ADJUSTMENT', 'COUNT']).optional().catch(undefined),
})

// ── stock transfers ──────────────────────────────────────────────────────────

export const TRANSFER_PAGE_SIZES = [25, 50, 100] as const
const TRANSFER_SORT_FIELDS = ['transferDate', 'number'] as const
export type TransferSort = (typeof TRANSFER_SORT_FIELDS)[number] | `-${(typeof TRANSFER_SORT_FIELDS)[number]}`

export function isTransferSort(value: string): value is TransferSort {
  return (TRANSFER_SORT_FIELDS as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
}

export const TRANSFER_DEFAULT_SORT = '-transferDate'

/** The transfers list lives in the URL; invalid values fall back to the default. */
export const transferSearchSchema = z.object({
  page,
  pageSize: pageSizeOf(TRANSFER_PAGE_SIZES),
  sort: z.string().refine(isTransferSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  status: z.enum(['DRAFT', 'POSTED', 'VOID']).optional().catch(undefined),
  /** Transfers out of or into this warehouse. */
  warehouseId: z.uuid().optional().catch(undefined),
  from: isoDate,
  to: isoDate,
})
export type TransferSearch = z.infer<typeof transferSearchSchema>

export interface TransferListParams {
  page: number
  pageSize: number
  sort: string
  q: string | undefined
  status: 'DRAFT' | 'POSTED' | 'VOID' | undefined
  warehouseId: string | undefined
  from: string | undefined
  to: string | undefined
}

export function toTransferListQuery(search: TransferSearch): TransferListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? TRANSFER_PAGE_SIZES[0],
    sort: search.sort ?? TRANSFER_DEFAULT_SORT,
    q: search.q || undefined,
    status: search.status,
    warehouseId: search.warehouseId,
    from: search.from,
    to: search.to,
  }
}

/**
 * New transfer: `?fromWarehouseId=` preselects the warehouse the goods leave, `?productId=` fills the
 * lines with the product's active variants (Transfer from a product).
 */
export const newTransferSearchSchema = z.object({
  fromWarehouseId: z.uuid().optional().catch(undefined),
  productId: z.uuid().optional().catch(undefined),
  variantId: z.uuid().optional().catch(undefined),
})

/** Transfer print: `?print=1` prints as soon as the sheet is ready (the Print action). */
export const transferPrintSearchSchema = z.object({
  print: z.coerce.boolean().optional().catch(undefined),
})
